import React, { useEffect, useMemo, useState } from 'react';
import useEeaArticles from '../hooks/use-eea-articles';
import {
  Field,
  Fieldset,
  Heading,
  Label,
  Radio,
  RichTextEditor,
  ValidationMessage,
} from '@innovationnorway/cl-react';
import DataTableV3 from 'src/components/DataTableV3';
import { eeaArticleColumns } from '../constants/eea-article-columns';
import useToggle from 'src/hooks/use-toggle';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import SelectEeaArticleTable from '../components/SelectEeaArticleTable';
import { Controller } from 'react-hook-form';
import useEeaForm, { EeaData } from '../hooks/subforms/use-eea-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import { MutableRefObject } from 'react';
import AddEditButton from '../components/AddEditButton';
import { EEA_COMMENTS_MAX_LENGTH } from '../constants/service-form-max-lengths';

type EEAPageProps = SubFormProps<EeaData> & {
  eeaCommentsRawTextRef: MutableRefObject<string>;
};

const EEAPage = ({ eeaCommentsRawTextRef, ...props }: EEAPageProps) => {
  const { form, rules } = useEeaForm({ ...props, eeaCommentsRawTextRef });
  const { control, setValue, getValues, watch } = form;
  const { t: tServices } = useTranslation('services');
  const { eeaArticles, isLoading } = useEeaArticles();

  const selectedEeaArticles = watch('selectedEeaArticles');

  const selectedArticles = useMemo(() => {
    return eeaArticles?.filter((article) =>
      selectedEeaArticles?.map((eea) => eea.eeaArticleId).includes(article.id),
    );
  }, [eeaArticles, selectedEeaArticles]);

  const [usesEeaArticles, setUsesEeaArticles] = useState<boolean | undefined>(
    selectedEeaArticles.length > 0 ? true : undefined,
  );

  useEffect(() => {
    if (selectedEeaArticles.length > 0 && !usesEeaArticles) setUsesEeaArticles(true);
  }, [selectedEeaArticles, usesEeaArticles]);

  const { isActive, activate, deactivate } = useToggle(false);
  const { errors } = props;

  return (
    <div>
      <Heading level={1}>{tServices('eea.title')}</Heading>
      <div className="display--flex flex-direction--column gap--2 margin-top--2">
        <div>
          <Fieldset>
            <Fieldset.Legend data-size="md">{tServices('eea.applies.label')}</Fieldset.Legend>
            <Fieldset.Description>{tServices('eea.applies.help')}</Fieldset.Description>
            <Radio
              label={tServices('form.yes')}
              name="usesEeaArticles"
              value="true"
              checked={usesEeaArticles === true}
              onChange={() => {
                setUsesEeaArticles(true);
                setValue('selectedEeaArticles', []);
              }}
            />
            <Radio
              label={tServices('form.no')}
              name="usesEeaArticles"
              value="false"
              checked={usesEeaArticles === false}
              onChange={() => {
                setUsesEeaArticles(false);
                setValue('selectedEeaArticles', []);
              }}
            />
          </Fieldset>
        </div>
        {usesEeaArticles || selectedEeaArticles?.length > 0 ? (
          <div>
            <Field>
              <Label>{tServices('eea.articles.question')}</Label>
              <Field.Description data-size="sm">{tServices('eea.articles.help')}</Field.Description>
            </Field>
            {eeaArticles && (
              <>
                <DataTableV3
                  disableSelection
                  disableColumnEditor
                  disableGlobalFilter
                  disablePagination
                  disableExport
                  hideFilterText
                  hideScrollbar
                  data={selectedArticles || []}
                  columns={eeaArticleColumns}
                  id={'eeaArticles'}
                  placeholderText={tServices('eea.articles.placeholder')}
                />
                <AddEditButton
                  className="padding--2"
                  hasSelection={selectedArticles !== undefined && selectedArticles.length > 0}
                  addLabel={tServices('form.add')}
                  editLabel={tServices('form.edit.button')}
                  onClick={() => activate()}
                />
              </>
            )}
            <div className="margin-top--5">
              <Controller
                control={control}
                name="eeaComments"
                rules={rules.eeaComments}
                render={({ field, fieldState }) => (
                  <Field>
                    <Label>{tServices('eea.comments.label')}</Label>
                    <Field.Description data-size="sm">{tServices('eea.comments.help')}</Field.Description>
                    <RichTextEditor
                      onChange={(value) => field.onChange(value)}
                      onChangeRawText={(rawText) => {
                        eeaCommentsRawTextRef.current = rawText;
                      }}
                      onBlur={field.onBlur}
                      initialValue={field.value}
                      rows={6}
                      counter={EEA_COMMENTS_MAX_LENGTH}
                    />
                    {(fieldState.error?.message || errors.eeaComments?.message) && (
                      <ValidationMessage>
                        {fieldState.error?.message || errors.eeaComments?.message}
                      </ValidationMessage>
                    )}
                  </Field>
                )}
              />
            </div>
            <DialogContainer
              show={isActive}
              onClose={() => deactivate()}
              title={tServices('eea.articles.dialogTitle')}
            >
              <SelectEeaArticleTable
                setValue={setValue}
                getValues={getValues}
                eeaArticles={eeaArticles}
                isLoading={isLoading}
                close={deactivate}
              />
            </DialogContainer>
          </div>
        ) : (
          <div>
            <Controller
              control={control}
              name="eeaComments"
              render={({ field, fieldState }) => (
                <Field>
                  <Label>{tServices('eea.comments.outsideLabel')}</Label>
                  <Field.Description data-size="sm">
                    {tServices('eea.comments.outsideHelp')}
                  </Field.Description>
                  <RichTextEditor
                    onChange={(value) => field.onChange(value)}
                    onChangeRawText={(rawText) => {
                      eeaCommentsRawTextRef.current = rawText;
                    }}
                    onBlur={field.onBlur}
                    initialValue={field.value}
                    rows={6}
                    counter={EEA_COMMENTS_MAX_LENGTH}
                  />
                  {(fieldState.error?.message || errors.eeaComments?.message) && (
                    <ValidationMessage>
                      {fieldState.error?.message || errors.eeaComments?.message}
                    </ValidationMessage>
                  )}
                </Field>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EEAPage;
